import { DateFormat, Number, Space, Text } from "components/common"

import { IconText } from "components/custom"
import { List } from "antd"
import cx from "clsx"
import styles from "./index.module.less"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { getAreaUnit, convertToSpecificAreaUnit, getCurrencySymbol } from "utils"
import { useSelector } from "react-redux"

const InventoryDetail = ({ inventory }) => {
  const { t } = useTranslation()
  const userInfo = useSelector(state => state.app.user?.info)
  const userPlatformKey = userInfo?.AgencyConfiguration?.Platform?.platformKey
  const currency = useSelector(
    state =>
      state?.app?.user?.info?.Agent?.AgencySettings?.find(currency => currency.key === "primary_currency").currencyCode
  )

  const renderFeature = feature => {
    if (feature.length) {
      return feature.map((e, index) => (
        <Space size={8} align="top" key={index}>
          <div>{e?.Feature.name}</div>
          <div className="gray-700">
            <Number
              isCurrency={e.amountType === "fixed" ? true : false}
              value={e.amount}
              color="#9d9d9d"
              wrapValue={value => (e.amountType === "fixed" ? <>({value})</> : `(${value}%)`)}
            />
          </div>
        </Space>
      ))
    } else {
      return "-"
    }
  }

  const inventorySummary = useMemo(() => {
    return [
      [
        `${t("Land Area")} (${getAreaUnit(userPlatformKey)})`,
        (
          <Number
            showWholeNumber
            tooltip
            digitAfterFraction={4}
            value={convertToSpecificAreaUnit(inventory.landArea, userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f")}
          />
        ) || "-",
      ],
      [
        `${t("Gross Area")} (${getAreaUnit(userPlatformKey)})`,
        (
          <Number
            showWholeNumber
            tooltip
            digitAfterFraction={4}
            value={convertToSpecificAreaUnit(inventory.grossArea, userPlatformKey === "bayut_ksa" ? "sq_m" : "sq_f")}
          />
        ) || "-",
      ],
      [t("No of Beds"), inventory?.bed || "-"],
      [t("No of Baths"), inventory?.bath || "-"],
      [t("State"), inventory?.ListingAllocationType.name || "-"],
      [t("CDP"), inventory?.cdp || "-"],
      [t("Feature (Additional)"), renderFeature(inventory.additionalFeature), styles.listSingle],
      [t("Feature (Fixed)"), renderFeature(inventory.fixedFeature), styles.listSingle],
    ]
  }, [inventory])

  const bookingSummary = useMemo(() => {
    const bookingForm = inventory?.ContractListings?.[0]?.Contract?.InternalInquiry?.Documents?.[0]?.BookingForm
    const bookingFormStatus =
      inventory?.ContractListings?.[0]?.Contract?.InternalInquiry?.Documents?.[0]?.Disposition?.key
    const cdp = inventory?.ContractListings?.cdpCommitment || inventory?.bookingInfo?.cdp
    const installmentInfo = inventory?.bookingInfo?.instalment
    const installmentAmount = bookingForm?.paymentPlanYears != "-1" && installmentInfo?.instalmentAmount
    const installmentType = t(installmentInfo?.instalmentType?.replace("ly", "") || "month")
    const installmentCount =
      installmentInfo?.instalmentFrequency === 0
        ? installmentInfo?.instalmentMonths
        : installmentInfo?.instalmentFrequency
    const installmentDuration =
      bookingForm && bookingFormStatus !== "draft"
        ? bookingForm.paymentPlanYears > 0 || bookingForm.paymentPlanMonths > 0
          ? `${bookingForm?.paymentPlanYears ? `${bookingForm?.paymentPlanYears} ${t("years")}` : ""} ${
              bookingForm?.paymentPlanMonths ? `${bookingForm?.paymentPlanMonths} ${t("months")}` : ""
            }`
          : "-"
        : `${installmentInfo?.instalmentMonths ? `${installmentInfo?.instalmentMonths}  ${t("months")}` : "-"} `

    return [
      [
        t("Min. Token"),
        inventory?.bookingInfo?.token ? (
          <>
            {getCurrencySymbol()} {inventory?.bookingInfo?.token}
          </>
        ) : (
          "-"
        ),
      ],
      ...(inventory?.bookingInfo?.totalVASAmount !== null
        ? [[t("BSF Fee"), <Number digitAfterFraction={2} value={inventory?.bookingInfo?.totalVASAmount} isCurrency />]]
        : []),
      [
        t("PDP"),
        inventory?.bookingInfo?.pdp ? (
          <Number digitAfterFraction={2} value={inventory?.bookingInfo?.pdp} wrapValue={e => `${e}%`} />
        ) : (
          "-"
        ),
      ],
      [t("CDP"), cdp ? <Number digitAfterFraction={2} value={cdp} wrapValue={e => `${e}%`} /> : "-"],
      [
        t("Possession Amount"),
        inventory?.bookingInfo?.possession?.possessionPercentage ? (
          <Number
            digitAfterFraction={2}
            value={inventory?.bookingInfo?.possession?.possessionPercentage}
            wrapValue={e => `${e}%`}
          />
        ) : (
          "-"
        ),
      ],
      [
        t("Installment Amount"),
        !!installmentAmount ? (
          <>
            {getCurrencySymbol()} {installmentAmount?.toFixed(2)}
            {installmentCount !== 0 && ` / ${installmentType}`}
          </>
        ) : (
          "-"
        ),
      ],

      [t("Installment Duration"), installmentDuration],
      [
        t("Possession From"),
        inventory?.bookingInfo?.possession?.possessionFrom ? (
          <DateFormat date={inventory?.bookingInfo?.possession?.possessionFrom} dateClassName="" />
        ) : (
          "-"
        ),
      ],
    ]
  }, [inventory])

  const developerInfo = useMemo(() => {
    return [
      [t("Added By"), inventory?.AddedBy.name || "-"],
      [t("Updated By"), inventory?.UpdatedBy.name || "-"],
      [t("Date Added"), <DateFormat date={inventory?.createdAt} dateClassName="black" /> || "-"],
      [t("Date Updated"), <DateFormat date={inventory?.updatedAt} dateClassName="black" /> || "-"],
      [t("Status Updated By"), inventory?.StatusUpdatedBy?.name || "-"],
      [t("Date status Updated"), <DateFormat date={inventory?.statusUpdatedDate} dateClassName="" /> || "-"],
      [t("Status Comment"), <Text>{inventory?.comments}</Text> || "-"],
    ]
  }, [inventory])

  return (
    <>
      <IconText
        title={t("Inventory Info")}
        icon={"BsFileEarmarkText"}
        iconProps={{ style: { color: "#9D9D9D" }, hasBackground: true, size: "1em" }}
        gap={8}
        style={{ fontWeight: 600 }}
        className="mbe-12"
      />
      <List
        size="small"
        className={cx(styles.listingDetail, "mbe-32")}
        dataSource={inventorySummary}
        renderItem={e => (
          <List.Item className={e[2]}>
            <div className="gray-700">{e[0]}</div>
            <div>{e[1]}</div>
          </List.Item>
        )}
      />
      <IconText
        title={t("Booking Info")}
        icon={"BsFileEarmarkText"}
        iconProps={{ style: { color: "#9D9D9D" }, hasBackground: true, size: "1em" }}
        gap={8}
        style={{ fontWeight: 600 }}
        className="mbe-12"
      />
      <List
        size="small"
        className={cx(styles.listingDetail, "mbe-32")}
        dataSource={bookingSummary}
        renderItem={e => (
          <List.Item className={e[2]}>
            <div className="gray-700">{e[0]}</div>
            <div>{e[1]}</div>
          </List.Item>
        )}
      />
    </>
  )
}

export default InventoryDetail
